
import { useEffect } from 'react';

interface SchemaOrgProps {
  type: 'Organization' | 'WebPage' | 'Service' | 'FAQPage' | 'LocalBusiness';
  data: Record<string, unknown>;
}

export function SchemaOrg({ type, data }: SchemaOrgProps) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    script.id = `schema-${type.toLowerCase()}`;
    
    const existing = document.getElementById(`schema-${type.toLowerCase()}`);
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(`schema-${type.toLowerCase()}`);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [type, data]);
  
  return null;
}

export function getLocalBusinessSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.bellportbranding.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/#localbusiness`,
    name: 'Bellport Branding',
    image: `${siteUrl}/logo.png`,
    url: siteUrl,
    telephone: '+1-631-640-7888',
    description: 'Bellport Branding is a Long Island digital agency helping small businesses grow with professional website design, video production, social media management, and cybersecurity services. Serving Nassau and Suffolk County.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Bellport',
      addressRegion: 'NY',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 40.7891,
      longitude: -73.1350
    },
    areaServed: [
      {
        '@type': 'County',
        name: 'Nassau County',
        containedInPlace: {
          '@type': 'State',
          name: 'New York'
        }
      },
      {
        '@type': 'County',
        name: 'Suffolk County',
        containedInPlace: {
          '@type': 'State',
          name: 'New York'
        }
      },
      { '@type': 'City', name: 'Huntington, NY' },
      { '@type': 'City', name: 'Babylon, NY' },
      { '@type': 'City', name: 'Islip, NY' },
      { '@type': 'City', name: 'Smithtown, NY' },
      { '@type': 'City', name: 'Brookhaven, NY' },
      { '@type': 'City', name: 'Oyster Bay, NY' },
      { '@type': 'City', name: 'Hempstead, NY' },
      { '@type': 'City', name: 'North Hempstead, NY' },
      { '@type': 'City', name: 'Riverhead, NY' },
      { '@type': 'City', name: 'Southampton, NY' },
      { '@type': 'City', name: 'East Hampton, NY' },
      { '@type': 'City', name: 'Southold, NY' },
      { '@type': 'City', name: 'Shelter Island, NY' },
      { '@type': 'City', name: 'Glen Cove, NY' },
      { '@type': 'City', name: 'Long Beach, NY' },
      { '@type': 'City', name: 'Freeport, NY' },
      { '@type': 'City', name: 'Valley Stream, NY' },
      { '@type': 'City', name: 'Massapequa, NY' },
      { '@type': 'City', name: 'Levittown, NY' },
      { '@type': 'City', name: 'Hicksville, NY' },
      { '@type': 'City', name: 'Commack, NY' },
      { '@type': 'City', name: 'Brentwood, NY' },
      { '@type': 'City', name: 'Central Islip, NY' },
      { '@type': 'City', name: 'Bay Shore, NY' },
      { '@type': 'City', name: 'Patchogue, NY' }
    ],
    priceRange: '$$',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00'
      }
    ],
    sameAs: [
      'https://www.facebook.com/bellportbranding',
      'https://www.instagram.com/bellportbranding',
      'https://www.linkedin.com/company/bellportbranding'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-631-640-7888',
      contactType: 'sales',
      areaServed: ['Nassau County', 'Suffolk County', 'Long Island'],
      availableLanguage: 'English'
    }
  };
}

export function getOrganizationSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.bellportbranding.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Bellport Branding',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: 'Long Island digital agency specializing in website design, video production, social media management, and cybersecurity for small businesses in Nassau and Suffolk County.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Bellport',
      addressRegion: 'NY',
      addressCountry: 'US'
    },
    telephone: '+1-631-640-7888',
    sameAs: [
      'https://www.facebook.com/bellportbranding',
      'https://www.instagram.com/bellportbranding',
      'https://www.linkedin.com/company/bellportbranding'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-631-640-7888',
      contactType: 'Customer Service',
      areaServed: 'Long Island, NY',
      availableLanguage: 'English'
    }
  };
}

export function getWebPageSchema(pageName: string, description: string) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.bellportbranding.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageName,
    description: description,
    url: siteUrl,
    publisher: {
      '@type': 'Organization',
      name: 'Bellport Branding',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`
      }
    },
    inLanguage: 'en-US',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Bellport Branding',
      url: siteUrl
    },
    about: {
      '@type': 'Thing',
      name: 'Website Design and Digital Marketing Services for Long Island Small Businesses'
    }
  };
}

export function getServiceSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.bellportbranding.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Digital Marketing and Web Design',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Bellport Branding',
      url: siteUrl,
      telephone: '+1-631-640-7888'
    },
    areaServed: [
      {
        '@type': 'State',
        name: 'New York',
        containsPlace: [
          { '@type': 'County', name: 'Nassau County' },
          { '@type': 'County', name: 'Suffolk County' }
        ]
      }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Long Island Digital Services for Small Businesses',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Website Design Long Island',
            description: 'Professional website design for Long Island small businesses. Custom, mobile-responsive websites that convert visitors into customers. Serving Nassau and Suffolk County.'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Ethical Hacking & Cybersecurity',
            description: 'Protect your Long Island business with comprehensive security testing, penetration testing, and vulnerability assessments.'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Social Media Management Long Island',
            description: 'Strategic social media management for Long Island small businesses. Build your brand and engage local customers in Nassau and Suffolk County.'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Video Production Long Island',
            description: 'Professional videography and video production for Long Island businesses. Commercial shoots, brand films, and promotional videos.'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Video Editing Services',
            description: 'Expert video editing with color grading, motion graphics, and sound design for Long Island businesses.'
          }
        }
      ]
    }
  };
}
